<template>
  <d-container
    fluid
    class="main-content-container p-0">
    <d-card
      class="calendar mb-0">
      <div class="calendar-heading">
        <div class="calendar-period">{{ period }}</div>
        <div class="calendar-nav-top">
          <a
            href="#prev-month"
            :class="{ 'calendar-nav-top-prev': true, 'is-inactive': !previousMonthIsAvailable }"
            @click.prevent="goToPreviousMonth"
            :title="previousMonthName">
          </a>

          <d-button
            outline
            theme="primary"
            @click.prevent="showToday"
            :title="$t('calendar.today')">
            {{ $t('calendar.showToday') }}
          </d-button>

          <a
            href="#next-month"
            class="calendar-nav-top-next"
            @click.prevent="goToNextMonth"
            :title="nextMonthName">
          </a>
        </div>
      </div>

      <div class="calendar-heading-days">
        <div class="calendar-heading-day">{{ daysNames.day1.shortPart1 }}<span>{{ daysNames.day1.shortPart2 }}</span></div>
        <div class="calendar-heading-day">{{ daysNames.day2.shortPart1 }}<span>{{ daysNames.day2.shortPart2 }}</span></div>
        <div class="calendar-heading-day">{{ daysNames.day3.shortPart1 }}<span>{{ daysNames.day3.shortPart2 }}</span></div>
        <div class="calendar-heading-day">{{ daysNames.day4.shortPart1 }}<span>{{ daysNames.day4.shortPart2 }}</span></div>
        <div class="calendar-heading-day">{{ daysNames.day5.shortPart1 }}<span>{{ daysNames.day5.shortPart2 }}</span></div>
        <div class="calendar-heading-day">{{ daysNames.day6.shortPart1 }}<span>{{ daysNames.day6.shortPart2 }}</span></div>
        <div class="calendar-heading-day">{{ daysNames.day7.shortPart1 }}<span>{{ daysNames.day7.shortPart2 }}</span></div>
      </div>

      <div
        :class="{ 'calendar-days': true }">
        <calendar-day
          v-for="(dayData, index) of visibleDays"
          :key="'day-item-' + index"
          :dayData="dayData"
          :ordersData="itemsByDate[dayData.date]" />
      </div>
    </d-card>
  </d-container>
</template>

<script>
import CalendarDay from './CalendarDay';
import CalendarUtils from './../../utils/CalendarUtils';
import DaysNames from './../../data/days-names.js';
import getPrefferedLanguge from './../../utils/i18nUtils';

export default {
  name: 'calendar',
  props: [
    'loadedItems'
  ],
  components: {
    'calendar-day': CalendarDay
  },
  data () {
    return {
      itemsByDate: {},
      currentYear: null,
      currentMonth: null
    };
  },
  watch: {
    'loadedItems': function () {
      this.generateItems();
    }
  },
  computed: {
    daysNames () {
      return DaysNames[getPrefferedLanguge()];
    },
    period () {
      return CalendarUtils.getMonthName(this.currentMonth) + ' ' + this.currentYear;
    },
    visibleDays () {
      return CalendarUtils.getDaysList(this.currentYear, this.currentMonth);
    },
    previousMonthIsAvailable () {
      if (this.currentMonth === 1 && this.currentYear === 2020) {
        return false;
      }

      return true;
    },
    previousMonthName () {
      if (parseInt(this.currentMonth, 10) === 1) {
        return CalendarUtils.getMonthName(12) + ' ' + (this.currentYear - 1);
      }

      return CalendarUtils.getMonthName(this.currentMonth - 1) + ' ' + this.currentYear;
    },
    nextMonthName () {
      if (parseInt(this.currentMonth, 10) === 12) {
        return CalendarUtils.getMonthName(1) + ' ' + (this.currentYear + 1);
      }

      return CalendarUtils.getMonthName(this.currentMonth + 1) + ' ' + this.currentYear;
    },
    currentPeriod () {
      return {
        date_from: CalendarUtils.getStartDay(this.currentYear, this.currentMonth),
        date_to: CalendarUtils.getEndDay(this.currentYear, this.currentMonth)
      }
    }
  },
  mounted () {
    let today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth() + 1;
  },
  methods: {
    goToPreviousMonth () {
      let currentYear = parseInt(this.currentYear, 10);
      let currentMonth = parseInt(this.currentMonth, 10);
      currentMonth--;

      if (currentMonth === 0) {
        currentMonth = 12;
        currentYear--;
      }

      this.updatePeriod(currentYear, currentMonth);
    },
    goToNextMonth () {
      let currentYear = parseInt(this.currentYear, 10);
      let currentMonth = parseInt(this.currentMonth, 10);
      currentMonth++;

      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }

      this.updatePeriod(currentYear, currentMonth);
    },
    updatePeriod (newYear, newMonth) {
      this.currentYear = parseInt(newYear, 10);
      this.currentMonth = parseInt(newMonth, 10);
      this.$bus.$emit('services-schedule-calendar-refresh');
    },
    showToday () {
      let today = new Date();
      this.updatePeriod(today.getFullYear(), today.getMonth() + 1);
    },
    generateItems () {
      if (!this.loadedItems || !this.loadedItems.length) {
        this.itemsByDate = {};
      }

      let items = {};

      for (let i = 0; i < this.loadedItems.length; i++) {
        let date = this.loadedItems[i].date;

        items[date] = {
          items: this.loadedItems[i].items,
          orders_qty: parseInt(this.loadedItems[i].orders_count, 10),
          qty: parseInt(this.loadedItems[i].quantities_count, 10)
        };
      }

      this.itemsByDate = JSON.parse(JSON.stringify(items));
    }
  }
}
</script>

<style lang="scss" scoped>
.calendar {
  background: #FFF;
  border: 1px solid #CACEDB;
  margin: 0 auto 50px auto;
  max-width: 1400px;
  padding: 0;
  position: relative;
  width: 100%;

  &-heading {
    align-items: center;
    display: flex;
    padding: 2rem;
    position: relative;
    width: 100%;

    h1 {
      font-size: 2.4rem;
      font-weight: 400;
      line-height: 1;
      margin: 0;

      span {
        color: #007bff;
      }
    }

    & > .button {
      margin-left: auto;
      margin-right: 0;
    }

    .calendar-period {
      font-size: 20px;
      text-transform: capitalize;
    }
  }

  &-heading-days {
    display: flex;
  }

  &-heading-day {
    align-items: center;
    background: #fff;
    border: 1px solid #e1e5eb;
    border-left: none;
    border-right: none;
    color: #3D5170;
    display: flex;
    font-size: 1.1rem;
    font-weight: 500;
    justify-content: center;
    height: 3rem;
    text-align: center;
    width: calc(100% / 7);

    &:nth-child(even) {
      background: #f0f0f0;
      border-left: 1px solid #e1e5eb;
      border-right: 1px solid #e1e5eb;
      left: -1px;
      position: relative;
    }
  }

  &-days {
    display: flex;
    flex-wrap: wrap;
  }

  &-nav {
    align-items: center;
    display: flex;
    height: 80px;
    justify-content: center;

    .button {
      justify-content: center;
      width: 150px;
    }

    &-top {
      display: flex;
      left: 50%;
      padding-top: 1px;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);

      &-prev,
      &-next {
        width: 20px;

        &:before,
        &:after {
          background: #007bff;
          content: "";
          display: block;
          height: 2px;
          position: absolute;
          top: 50%;
          transform-origin: 0 50%;
          width: 14px;
        }
      }

      &-prev {
        margin-right: 20px;

        &:before,
        &:after {
          left: 0;
        }

        &:before {
          transform: rotate(-45deg);
        }

        &:after {
          transform: rotate(45deg);
        }

        &.is-inactive {
          opacity: .25;
          pointer-events: none;
        }
      }

      &-next {
        margin-left: 20px;

        &:before,
        &:after {
          transform-origin: 100% 50%;
          right: 0;
        }

        &:before {
          transform: rotate(45deg);
        }

        &:after {
          transform: rotate(-45deg);
        }
      }
    }
  }
}
</style>
